/**
 * Template Customizer - Sprint 19: Full Template Editor
 * Complete UI for creating, editing, and duplicating custom templates
 * 
 * Replaces the stub implementation with full scene editing capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Select, Button, message, Row, Col, Alert, Segmented, notification } from 'antd';
import { 
  Palette,
  Plus,
  Info,
  List,
  X,
  Save
} from 'lucide-react';
import { Template, TemplateCategory } from '../../../../types/template';
import { useTemplateEditor } from '../../hooks/useTemplateEditor';
import { SceneListEditor } from './SceneListEditor';
import { SceneConfigPanel } from './SceneConfigPanel';
import { PROJECT_LIMITS } from '../../config/projectLimits';
import './TemplateCustomizer.scss';

const { TextArea } = Input;

interface TemplateCustomizerProps {
  visible: boolean;
  onClose: () => void;
  template?: Template;
  mode: 'create' | 'edit' | 'duplicate';
  onSaveSuccess?: (templateId: string) => void;
}

const CATEGORY_OPTIONS: { value: TemplateCategory; label: string }[] = [
  { value: 'education', label: 'Education' },
  { value: 'business', label: 'Business' },
  { value: 'content', label: 'Content Creator' },
  { value: 'developer', label: 'Developer' },
  { value: 'interview', label: 'Interview' },
];

export const TemplateCustomizer: React.FC<TemplateCustomizerProps> = ({
  visible,
  onClose,
  template,
  mode,
  onSaveSuccess
}) => {
  const {
    editingTemplate,
    isDirty,
    isValid,
    validationErrors,
    activeSceneIndex,
    activeScene,
    activeScript,
    setActiveSceneIndex,
    initCreate,
    initDuplicate,
    initEdit,
    updateField,
    addScene,
    removeScene,
    updateScene,
    duplicateScene,
    reorderScenes,
    updateScriptForScene,
    closeEditor,
    saveTemplate,
    getTotalDuration
  } = useTemplateEditor();

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'scenes'>('info');

  const handleAddScene = useCallback(() => {
    const result = addScene();
    if (result && result.reason === 'max_scenes_reached') {
      notification.warning({
        message: 'Scene Limit Reached',
        description: `Templates can have a maximum of ${PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE} scenes.`,
        placement: 'topRight',
      });
    }
  }, [addScene]);

  const handleDuplicateScene = useCallback((index: number) => {
    const result = duplicateScene(index);
    if (result && result.reason === 'max_scenes_reached') {
      notification.warning({
        message: 'Scene Limit Reached',
        description: `Templates can have a maximum of ${PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE} scenes.`,
        placement: 'topRight',
      });
    }
  }, [duplicateScene]);

  useEffect(() => {
    if (visible) {
      if (mode === 'create') {
        initCreate();
      } else if (mode === 'duplicate' && template) {
        initDuplicate(template);
      } else if (mode === 'edit' && template) {
        initEdit(template);
      }
    }
  }, [visible, mode, template, initCreate, initDuplicate, initEdit]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const savedId = await saveTemplate();
      
      if (savedId) {
        message.success(
          mode === 'create' ? 'Template created successfully!' :
          mode === 'duplicate' ? 'Template duplicated successfully!' :
          'Template updated successfully!'
        );
        onSaveSuccess?.(savedId);
        onClose();
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Discard Changes',
        cancelText: 'Keep Editing',
        onOk: () => {
          closeEditor();
          onClose();
        }
      });
    } else {
      closeEditor();
      onClose();
    }
  };

  const handleAddFirstScene = () => {
    handleAddScene();
    setActiveTab('scenes');
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'Create Custom Template';
      case 'edit': return 'Edit Template';
      case 'duplicate': return 'Duplicate Template';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'create': return 'Design your own reusable video template';
      case 'edit': return 'Modify your template settings and scenes';
      case 'duplicate': return 'Create a copy with your own customizations';
    }
  };

  const hasErrors = Object.keys(validationErrors).length > 0;
  const sceneCount = editingTemplate?.scenes?.length || 0;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleClose}
      width={1000}
      className="template-customizer-modal"
      footer={null}
      closable={false}
    >
      <div className="modal-header">
        <div className="header-left">
          <div className="header-icon">
            <Palette size={20} />
          </div>
          <div className="header-text">
            <h2>{getTitle()}</h2>
            <p>{getSubtitle()}</p>
          </div>
        </div>
        <button className="close-button" onClick={handleClose}>
          <X size={20} />
        </button>
      </div>

      <div className="template-customizer-content">
        {hasErrors && (
          <Alert
            type="warning"
            message="Please fix the following issues before saving"
            description={
              <ul className="error-list">
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            className="validation-alert"
            showIcon
          />
        )}

        <div className="tab-navigation">
          <Segmented
            value={activeTab}
            onChange={(value) => setActiveTab(value as 'info' | 'scenes')}
            options={[
              {
                value: 'info',
                label: (
                  <div className="tab-label">
                    <Info size={16} />
                    <span>Template Info</span>
                  </div>
                ),
              },
              {
                value: 'scenes',
                label: (
                  <div className="tab-label">
                    <List size={16} />
                    <span>Scenes ({sceneCount})</span>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {activeTab === 'info' ? (
          <div className="template-info-section">
            <div className="form-row three-col">
              <div className="form-group">
                <label>Template Title <span className="required">*</span></label>
                <Input
                  value={editingTemplate?.title || ''}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Enter template title"
                  status={validationErrors['title'] ? 'error' : undefined}
                />
              </div>
              <div className="form-group">
                <label>Category <span className="required">*</span></label>
                <Select
                  value={editingTemplate?.category}
                  onChange={(value) => updateField('category', value)}
                  options={CATEGORY_OPTIONS}
                  placeholder="Select category"
                  style={{ width: '100%' }}
                  status={validationErrors['category'] ? 'error' : undefined}
                />
              </div>
              <div className="form-group">
                <label>Aspect Ratio</label>
                <Select
                  value={editingTemplate?.aspectRatio || '16:9'}
                  onChange={(value) => updateField('aspectRatio', value)}
                  options={[
                    { value: '16:9', label: '16:9 (Landscape)' },
                    { value: '9:16', label: '9:16 (Portrait)' },
                    { value: '1:1', label: '1:1 (Square)' },
                  ]}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description <span className="required">*</span></label>
              <TextArea
                value={editingTemplate?.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe what this template is for and what users will create..."
                rows={3}
                status={validationErrors['description'] ? 'error' : undefined}
              />
            </div>

            <div className="form-row two-col">
              <div className="form-group">
                <label>Example Output Title <span className="required">*</span></label>
                <Input
                  value={editingTemplate?.exampleOutput?.title || ''}
                  onChange={(e) => updateField('exampleOutput', {
                    ...editingTemplate?.exampleOutput,
                    title: e.target.value,
                    duration: getTotalDuration(),
                    whatUserGets: editingTemplate?.exampleOutput?.whatUserGets || ''
                  })}
                  placeholder="e.g., Product Launch Video"
                  status={validationErrors['exampleOutput.title'] ? 'error' : undefined}
                />
              </div>
              <div className="form-group">
                <label>What User Gets</label>
                <Input
                  value={editingTemplate?.exampleOutput?.whatUserGets || ''}
                  onChange={(e) => updateField('exampleOutput', {
                    ...editingTemplate?.exampleOutput,
                    title: editingTemplate?.exampleOutput?.title || '',
                    duration: getTotalDuration(),
                    whatUserGets: e.target.value
                  })}
                  placeholder="e.g., A professional product demo video"
                />
              </div>
            </div>

            {sceneCount === 0 && (
              <div className="empty-scenes-prompt">
                <img 
                  src="/images/empty-projects-icon.png" 
                  alt="No scenes" 
                  className="empty-icon"
                />
                <h3>No Scenes Yet</h3>
                <p>Add your first scene to build your template structure.</p>
                <Button 
                  type="primary" 
                  icon={<Plus size={14} />} 
                  onClick={handleAddFirstScene}
                >
                  Add First Scene
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="scenes-editor-section">
            <Row gutter={16}>
              <Col span={8}>
                <SceneListEditor
                  scenes={editingTemplate?.scenes || []}
                  activeSceneIndex={activeSceneIndex}
                  onSelectScene={setActiveSceneIndex}
                  onAddScene={handleAddScene}
                  onRemoveScene={removeScene}
                  onDuplicateScene={handleDuplicateScene}
                  onReorderScene={reorderScenes}
                />
              </Col>
              <Col span={16}>
                {activeScene ? (
                  <SceneConfigPanel
                    scene={activeScene}
                    script={activeScript}
                    sceneIndex={activeSceneIndex}
                    category={editingTemplate?.category || 'content'}
                    aspectRatio={editingTemplate?.aspectRatio || '16:9'}
                    onUpdateScene={(updates) => updateScene(activeSceneIndex, updates)}
                    onUpdateScript={(content) => updateScriptForScene(activeSceneIndex, content)}
                    onUpdateAspectRatio={(ratio) => updateField('aspectRatio', ratio)}
                  />
                ) : (
                  <div className="no-scene-selected">
                    <img 
                      src="/images/empty-projects-icon.png" 
                      alt="No scene selected" 
                      className="empty-icon"
                    />
                    <h3>No Scene Selected</h3>
                    <p>Select a scene from the list to configure it, or add a new scene.</p>
                    <Button 
                      type="primary" 
                      icon={<Plus size={14} />} 
                      onClick={handleAddScene}
                    >
                      Add Scene
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        )}
      </div>

      <div className="modal-footer">
        <Button onClick={handleClose} icon={<X size={16} />}>
          Cancel
        </Button>
        <Button 
          type="primary" 
          loading={saving} 
          onClick={handleSave}
          icon={<Save size={16} />}
          disabled={!isValid}
        >
          {mode === 'create' ? 'Create Template' : 'Save Template'}
        </Button>
      </div>
    </Modal>
  );
};

export default TemplateCustomizer;
